import { useState } from 'react';
import { useAmplitude } from 'hooks/useAmplitude';
import {
  FieldValues,
  useFieldArray,
  UseFieldArrayAppend,
  useFormContext,
} from 'react-hook-form';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { PersonalDetailsType } from '../PersonalDetails/PersonalDetails';
import { TSignupFormData } from '../SignupForm/SignupForm';

export type TMultiAddressInput = {
  personRole: PersonalDetailsType['role'];
  personIndex: number;
};

export const useMultiAddressInput = ({
  personRole,
  personIndex,
}: TMultiAddressInput) => {
  const [targetAddressIndex, setTargetAddressIndex] = useState(0);
  const { trackEvent } = useAmplitude();

  const {
    formState: { errors },
  } = useFormContext<TSignupFormData>();

  const {
    fields: addresses,
    append: appendAddress,
    remove: removeAddress,
  } = useFieldArray({
    name:
      personRole === 'applicant'
        ? `applicant.residentialAddresses`
        : `guarantor.${personIndex}.residentialAddresses`,
  });

  const handleAppendAddress: UseFieldArrayAppend<
    FieldValues,
    | `applicant.residentialAddresses`
    | `guarantor.${number}.residentialAddresses`
  > = (data) => {
    if (addresses.length === 5) {
      return;
    }
    appendAddress(data);
    trackEvent(AMPLITUDE_EVENTS.extraDetailsAdded, {
      'Details added': 'Address',
      'CTA text': 'Add another address',
    });
    setTargetAddressIndex(addresses.length);
  };

  const handleRemoveAddress = (index: number) => {
    setTargetAddressIndex(Math.max(0, index - 1));
    removeAddress(index);
    trackEvent(AMPLITUDE_EVENTS.extraDetailsRemoved, {
      'Details removed': 'Address',
    });
  };

  return {
    addresses,
    additionalAddresses: addresses.length === 0 ? [] : addresses.slice(1),
    appendAddress: handleAppendAddress,
    removeAddress: handleRemoveAddress,
    errors,
    targetAddressIndex,
    setTargetAddressIndex,
  };
};
